import _extends from "@babel/runtime/helpers/extends";
import { ThemeProvider, StyledEngineProvider, createTheme as createMuiTheme, useTheme as muiUseTheme, alpha } from "@mui/material/styles";
import muiMakeStyles from "@mui/styles/makeStyles";
import muiCreateStyles from "@mui/styles/createStyles";
import Fade from "@mui/material/Fade";
import React from "react";
import classNames from "classnames";
import createBreakpoints from "./CreateBreakpoints";
import { grey } from "@mui/material/colors";
export const scrollbars = {
  main: {
    // Enable overflow
    overflow: "auto",
    // Webkit/Blink (Chrome/Safari)
    overflowX: "overlay",
    overflowY: "overlay",
    "&::-webkit-scrollbar": {
      width: "0.5rem"
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(0, 0, 0, 0.25)"
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.18)"
    },
    "&::-webkit-scrollbar-thumb:active": {
      backgroundColor: "rgba(0, 0, 0, 0.21)"
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "rgba(0, 0, 0, 0.05)"
    },
    // Firefox
    scrollbarColor: "rgba(0, 0, 0, 0.25) rgba(0, 0, 0, 0.05)",
    scrollbarWidth: "thin"
  }
};
const defaultTheme = {
  name: "CivicPlus",
  palette: {
    contrastThreshold: 4.5,
    primary: {
      main: "#CC0020"
    },
    secondary: {
      main: "#383838"
    },
    background: {
      blurred: "rgba(255,255,255,.75)",
      default: "#eee"
    },
    text: {
      secondary: `rgba(0,0,0,.6)`
    },
    default: {
      main: grey[300],
      dark: grey[400]
    }
  }
};
const makeActionColorFromHex = hex => {
  const [r, g, b] = hex.match(/\w\w/g).map(x => parseInt(x, 16));
  return `rgba(${r},${g},${b},.07)`;
};
export const themes = {
  default: defaultTheme,
  clerk: {
    name: "CivicClerk",
    palette: {
      ...defaultTheme.palette,
      primary: {
        main: "#7958E8"
      },
      secondary: {
        main: defaultTheme.palette.secondary.main
      }
    }
  },
  engageCentral: {
    name: "CivicEngage Central",
    palette: {
      ...defaultTheme.palette,
      primary: {
        main: "#006CF6"
      },
      secondary: {
        main: defaultTheme.palette.secondary.main
      }
    }
  },
  engageEvolve: {
    name: "CivicEngage Evolve",
    palette: {
      ...defaultTheme.palette,
      primary: {
        main: "#006CF6"
      },
      secondary: {
        main: defaultTheme.palette.secondary.main
      }
    }
  },
  hr: {
    name: "CivicHR",
    palette: {
      ...defaultTheme.palette,
      primary: {
        main: "#007EBC"
      },
      secondary: {
        main: defaultTheme.palette.secondary.main
      }
    }
  },
  optimize: {
    name: "CivicOptimize",
    palette: {
      ...defaultTheme.palette,
      primary: {
        main: "#008672"
      },
      secondary: {
        main: defaultTheme.palette.secondary.main
      }
    }
  },
  ready: {
    name: "CivicReady",
    palette: {
      ...defaultTheme.palette,
      primary: {
        main: "#BE4B00"
      },
      secondary: {
        main: defaultTheme.palette.secondary.main
      }
    }
  },
  rec: {
    name: "CivicRec",
    palette: {
      ...defaultTheme.palette,
      primary: {
        main: "#408100"
      },
      secondary: {
        main: defaultTheme.palette.secondary.main
      }
    }
  },
  service: {
    name: "CivicService",
    palette: {
      ...defaultTheme.palette,
      primary: {
        main: "#737373"
      },
      secondary: {
        main: defaultTheme.palette.secondary.main
      }
    }
  },
  scf: {
    name: "SeeClickFix",
    palette: {
      ...defaultTheme.palette,
      primary: {
        main: "#0099CC"
      },
      secondary: {
        main: defaultTheme.palette.secondary.main
      }
    }
  },
  preamble: {
    name: "Preamble",
    palette: {
      ...defaultTheme.palette,
      primary: {
        main: "#3A7470"
      },
      secondary: {
        main: defaultTheme.palette.secondary.main
      }
    }
  },
  accountService: {
    name: "Account Service",
    palette: defaultTheme.palette
  },
  orgService: {
    name: "Organization Service",
    palette: defaultTheme.palette
  },
  notifications: {
    name: "Notifications",
    palette: defaultTheme.palette
  },
  residentPortal: {
    name: "Resident Portal",
    palette: defaultTheme.palette
  },
  pay: {
    name: "Pay",
    palette: defaultTheme.palette
  },
  integrationHub: {
    name: "Integration Hub",
    palette: defaultTheme.palette
  }
};
export const getProductName = key => {
  if (key && key in themes) return themes[key].name;
  return "Unknown product";
};
export const createTheme = (product, primaryHex, title, cssReset, themeOverride, maxBreakpoint) => {
  const themePalette = product && product in themes ? JSON.parse(JSON.stringify(themes[product].palette)) : JSON.parse(JSON.stringify(themes.default.palette));
  let otherTypographyDefaults = {};
  let otherOverride = {};
  if (cssReset) {
    otherTypographyDefaults = {
      h1: {
        fontSize: "96px"
      },
      h2: {
        fontSize: "60px"
      },
      h3: {
        fontSize: "48px"
      },
      h4: {
        fontSize: "34px"
      },
      h5: {
        fontSize: "25px"
      },
      h6: {
        fontSize: "20px"
      },
      subtitle1: {
        fontSize: "16px"
      },
      subtitle2: {
        fontSize: "14px"
      },
      body1: {
        fontSize: "16px"
      },
      body2: {
        fontSize: "14px"
      },
      caption: {
        fontSize: "12px"
      },
      overline: {
        fontSize: "12px"
      }
    };
    otherOverride = {
      MuiSvgIcon: {
        root: {
          fontSize: "24px"
        },
        fontSizeSmall: {
          fontSize: "20px"
        },
        fontSizeLarge: {
          fontSize: "35px"
        }
      }
    };
  }
  if (typeof primaryHex !== "undefined" && primaryHex !== "") {
    themePalette.primary.main = primaryHex;
  }
  themeOverride = themeOverride || {};
  const defaultTheme = createMuiTheme();
  if (maxBreakpoint) {
    const theme = themeOverride?.breakpoints ? themeOverride : defaultTheme;
    if (theme.breakpoints) {
      const customBreakpoints = breakpointCappedToSize(theme.breakpoints, maxBreakpoint);
      themeOverride.breakpoints = {
        values: customBreakpoints
      };
    }
  }
  const containerBreakpoints = createBreakpoints({
    values: themeOverride.breakpoints?.values || defaultTheme.breakpoints.values,
    queryName: "@container"
  });
  const actionColor = makeActionColorFromHex(themePalette.primary.main);
  themePalette.action = {
    ...themePalette.action,
    selected: actionColor,
    hover: actionColor
  };
  const theme = createMuiTheme({
    palette: themePalette,
    typography: {
      fontFamily: "'Lato', 'Helvetica', 'Arial', sans-serif",
      button: {
        fontSize: ".75rem",
        letterSpacing: 1,
        wordBreak: "break-word"
      },
      ...otherTypographyDefaults
    }
  });
  const spacingNumber = spacing => Number(theme.spacing(spacing).slice(0, -2));
  return {
    ...createMuiTheme(theme, {
      ...themeOverride,
      components: {
        MuiInputBase: {
          styleOverrides: {
            input: {
              height: "auto"
            }
          }
        },
        MuiButton: {
          variants: [{
            props: {
              variant: "contained",
              color: "default"
            },
            style: {
              color: theme.palette.getContrastText(theme.palette.grey[300])
            }
          }, {
            props: {
              variant: "outlined",
              color: "default"
            },
            style: {
              color: theme.palette.text.primary,
              borderColor: theme.palette.mode === "light" ? "rgba(0, 0, 0, 0.23)" : "rgba(255, 255, 255, 0.23)",
              "&.Mui-disabled": {
                border: `1px solid ${theme.palette.action.disabledBackground}`
              },
              "&:hover": {
                borderColor: theme.palette.mode === "light" ? "rgba(0, 0, 0, 0.23)" : "rgba(255, 255, 255, 0.23)",
                backgroundColor: alpha(theme.palette.text.primary, theme.palette.action.hoverOpacity)
              }
            }
          }, {
            props: {
              color: "default",
              variant: "text"
            },
            style: {
              color: theme.palette.text.primary,
              "&:hover": {
                backgroundColor: alpha(theme.palette.text.primary, theme.palette.action.hoverOpacity)
              }
            }
          }]
        },
        MuiFormHelperText: {
          styleOverrides: {
            root: {
              "&.Mui-disabled": {
                color: "rgba(0,0,0,0.6)"
              }
            }
          }
        },
        MUIDataTableBodyCell: {
          styleOverrides: {
            root: {
              wordBreak: "break-word",
              textWrap: "balance"
            },
            stackedCommon: {
              // Tablet and Mobile cell styles
              [theme.breakpoints.down("md")]: {
                "&:nth-last-of-type(2)": {
                  fontWeight: 800,
                  textTransform: "uppercase"
                }
              },
              // Tablet cell styles
              [theme.breakpoints.only("sm")]: {
                verticalAlign: "middle",
                overflowWrap: "break-word",
                hyphens: "auto",
                "&:nth-last-of-type(2)": {
                  width: "30%",
                  paddingRight: theme.spacing(1)
                },
                "&:last-of-type": {
                  width: "70%",
                  textAlign: "right"
                }
              },
              // Mobile cell styles
              [theme.breakpoints.only("xs")]: {
                width: "100%",
                textAlign: "left"
              }
            }
          }
        },
        MUIDataTableBodyRow: {
          styleOverrides: {
            responsiveStacked: {
              [theme.breakpoints.down("md")]: {
                margin: theme.spacing(2, 1),
                display: "block",
                boxShadow: theme.shadows[2],
                borderBottom: "none",
                borderTop: "none",
                borderRadius: theme.shape.borderRadius
              }
            }
          }
        },
        MUIDataTableHeadCell: {
          styleOverrides: {
            root: {
              wordBreak: "normal",
              "& button": {
                wordBreak: "normal",
                fontSize: "1em",
                letterSpacing: "inherit"
              },
              [theme.breakpoints.down("md")]: {
                borderBottom: `1px solid ${theme.palette.divider}`,
                "&:last-of-type": {
                  borderBottom: "none"
                }
              }
            },
            sortAction: {
              alignItems: "center"
            },
            sortLabelRoot: {
              "& svg": {
                fontSize: theme.spacing(1.5)
              }
            }
          }
        },
        MUIDataTableSelectCell: {
          styleOverrides: {
            headerCell: {
              backgroundColor: "transparent"
            }
          }
        },
        MUIDataTableToolbar: {
          styleOverrides: {
            root: {
              paddingLeft: 0,
              paddingRight: 0
            },
            filterPaper: {
              // media query for Internet Explorer 11 blur when rendering 3d transform
              "@media screen and (-ms-high-contrast: active), (-ms-high-contrast: none)": {
                transform: "scale(1)"
              },
              // mui-datatables uses some negative margins, this accounts for those
              maxWidth: "calc(100% - 32px)",
              minWidth: 900,
              [theme.breakpoints.down("lg")]: {
                minWidth: 0,
                width: "100%"
              }
            }
          }
        },
        MUIDataTableToolbarSelect: {
          styleOverrides: {
            root: {
              padding: theme.spacing(0, 3.25),
              zIndex: 1,
              backgroundColor: theme.palette.background.paper
            },
            title: {
              paddingLeft: 0,
              paddingRight: theme.spacing(3.25)
            },
            iconButton: {
              marginRight: 0
            }
          }
        },
        MUIDataTableFilter: {
          styleOverrides: {
            root: {
              // Fix for IE filter panel not growing
              [`@media screen and (min-width: ${theme.breakpoints.values.md}px) and (-ms-high-contrast: active), screen and (min-width: ${theme.breakpoints.values.md}px) and (-ms-high-contrast: none)`]: {
                minWidth: 900
              }
            },
            gridListTile: {
              marginTop: 0
            }
          }
        },
        MUIDataTablePagination: {
          styleOverrides: {
            root: {
              [theme.breakpoints.down("md")]: {
                width: "100%"
              }
            },
            toolbar: {
              [theme.breakpoints.down("md")]: {
                justifyContent: "center",
                flexWrap: "wrap"
              }
            },
            actions: {
              [theme.breakpoints.down("md")]: {
                marginLeft: 0,
                width: "100%",
                display: "flex",
                justifyContent: "space-between"
              }
            },
            tableCellContainer: {
              borderBottom: "none"
            }
          }
        },
        MUIDataTable: {
          styleOverrides: {
            root: obj => {
              const isCompact = obj.compact ?? false;
              const haveOnRowClick = obj.options?.["onRowClick"] ?? false;
              return {
                "& .MuiTableCell-root": {
                  padding: isCompact ? theme.spacing(1.5) : theme.spacing(2)
                },
                "& tbody .MuiTableRow-root": {
                  backgroundColor: isCompact ? "transparent" : theme.palette.background.paper,
                  "&:hover": {
                    backgroundColor: isCompact ? "transparent" : theme.palette.action.hover,
                    cursor: haveOnRowClick ? "pointer" : undefined,
                    "&:focus": {
                      backgroundColor: haveOnRowClick ? theme.palette.action.hover : undefined
                    },
                    // Avoid hover effect on mobile
                    [theme.breakpoints.down("md")]: {
                      backgroundColor: theme.palette.background.paper
                    }
                  }
                },
                "& thead.MuiTableHead-root th.MuiTableCell-root": !isCompact ? {
                  paddingTop: theme.spacing(0.5),
                  paddingBottom: theme.spacing(0.5),
                  fontWeight: 800,
                  border: "none ",
                  textTransform: theme.typography.button.textTransform,
                  fontSize: theme.typography.fontSize
                } : {},
                "& thead.MuiTableHead-root th.MuiTableCell-root button": !isCompact ? {
                  paddingTop: theme.spacing(0.5),
                  paddingBottom: theme.spacing(0.5),
                  fontWeight: 800,
                  border: "none ",
                  textTransform: theme.typography.button.textTransform,
                  fontSize: theme.typography.fontSize
                } : {}
              };
            }
          }
        },
        ...themeOverride?.components,
        ...otherOverride
      }
    }),
    mixins: {
      ...defaultTheme.mixins,
      toolbar: {
        ...defaultTheme.mixins.toolbar,
        minHeight: 42
      }
    },
    scrollbars,
    title,
    containerBreakpoints,
    spacingNumber
  };
};
const routeTransitionStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexGrow: 1,
    flexDirection: "column"
  }
}));
export const RouteTransitionProvider = props => {
  const {
    children,
    className,
    ...otherProps
  } = props;
  const classes = routeTransitionStyles();
  return /*#__PURE__*/React.createElement(Fade, {
    in: true
  }, /*#__PURE__*/React.createElement("div", _extends({
    className: classNames("prmbl-transition-provider", className, classes.root)
  }, otherProps), children));
};
/**
 * @deprecated Use ProductThemeProvider with themeOverride directly instead.
 */
/* istanbul ignore next */
export const StyleWithOverrides = props => {
  const {
    theme,
    overrides,
    children
  } = props;
  const renderedTheme = {
    ...theme,
    components: {
      ...theme.components,
      ...overrides
    }
  };
  return /*#__PURE__*/React.createElement(StyledEngineProvider, {
    injectFirst: true
  }, /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: renderedTheme
  }, children));
};

// Most of this interface comes from MUI, just swapped the default theme to PreambleTheme
// eslint-disable-next-line @typescript-eslint/ban-types
export function makeStyles(styles, options) {
  return muiMakeStyles(styles, options);
}

// Most of this interface comes from MUI, just swapped the default theme to PreambleTheme
// eslint-disable-next-line @typescript-eslint/ban-types
export function createStyles(styles) {
  return muiCreateStyles(styles);
}
export const useTheme = () => {
  return muiUseTheme();
};
export const breakpointCappedToSize = (breakpoint, size) => {
  const customBreakpoints = breakpoint.values;
  const length = Object.keys(customBreakpoints).length;
  const index = Object.keys(customBreakpoints).indexOf(size);
  const positionsUpdate = length - index - 1;
  Object.entries(customBreakpoints).forEach(([key], index) => {
    const newPosition = index + positionsUpdate;
    const newValue = Object.values(customBreakpoints)[newPosition];
    if (newValue) {
      customBreakpoints[key] = newValue;
    } else {
      customBreakpoints[key] = 99999; // A size that will never be reached
    }
  });
  customBreakpoints["xs"] = 0; // Extra small size has to be always 0, otherwise the content disappears when it hit below that size
  return customBreakpoints;
};